import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, FileSpreadsheet, FolderOpen, RotateCcw, Undo2 } from 'lucide-react';
import { BarreSites } from '../components/ui/BarreSites';
import { ChampDate } from '../components/ui/ChampDate';
import { ChampFiltre, ChampRecherche } from '../components/ui/Onglets';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  Carte,
  Chargement,
  Compteur,
  EnteteEcran,
  MessageErreur,
  Pagination,
  Pastille,
  PastilleSite,
} from '../components/ui/Primitives';
import { useToast } from '../components/ui/useToast';
import { ControlesFacture, libelleAlertes } from '../components/factures-formateurs/ControlesFacture';
import { DossierFactureFormateur } from '../components/factures-formateurs/DossierFactureFormateur';
import { ModaleMotif } from '../components/factures-formateurs/GestesFactureFormateur';
import { usePerimetre } from '../site/usePerimetre';
import { ErreurApi } from '../lib/api';
import { useTaillePage } from '../lib/useTaillePage';
import { useValeurDifferee } from '../lib/useRecherche';
import { useRenvoyerFactureFormateur, useValiderFactureFormateur } from '../lib/useCircuitFactures';
import {
  exporterReception,
  periodeIncomplete,
  useReceptionFactures,
  type ChoixPeriodeReception,
  type EtatReception,
  type FiltresReception,
  type LigneBulletinDePaie,
  type LigneReception,
} from '../lib/useReceptionFactures';
import { dateReunion, euros, heures, jourCourt, messageRefus } from '../lib/useFormateurs';
import type { TypeFacture } from '../lib/useFormateurs';

type Filtres = Omit<FiltresReception, 'site'>;

const SANS_FILTRE: Filtres = { periode: 'MOIS', du: '', au: '', etat: '', type: '', recherche: '' };

const PERIODES_PAR_DEFAUT: readonly { choix: ChoixPeriodeReception; libelle: string }[] = [
  { choix: 'MOIS', libelle: 'Mois en cours' },
];

/** Une ligne du registre : une facture reçue, ou les heures d'un salarié. */
type Ligne = { nature: 'FACTURE'; facture: LigneReception } | { nature: 'PAIE'; paie: LigneBulletinDePaie };

const CLASSE_ACTION =
  'inline-flex min-h-[36px] shrink-0 items-center rounded-lg px-2 text-xs font-semibold text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[44px]';
const CLASSE_LIEN_PRINCIPAL =
  'inline-flex min-h-[44px] items-center justify-center rounded-xl bg-codialis-navy px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-codialis-deep';
const CLASSE_LIEN_SECONDAIRE =
  'inline-flex min-h-[44px] items-center justify-center rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50';
const CLASSE_TH = 'px-2.5 py-3 2xl:px-3 text-left text-[11px] font-bold uppercase tracking-wider text-codialis-gray';
const CLASSE_TD = 'px-2.5 py-3 align-top 2xl:px-3';
/**
 * « Statut prof. » a sa colonne sur grand écran ; en dessous, il passe sous le
 * nom : à côté de la carte de contrôle, le tableau tiendrait mal ses huit colonnes.
 */
const CLASSE_STATUT_LARGE = 'hidden 2xl:table-cell';

function pluriel(nombre: number, singulier: string, plurielTexte = `${singulier}s`): string {
  return `${nombre} ${nombre > 1 ? plurielTexte : singulier}`;
}

function tonType(type: TypeFacture | null): string {
  return type === 'CFA' ? 'info' : 'mint';
}

/**
 * Réception des factures formateurs (TF-402), sur la maquette `fac-formateurs` :
 * le registre du pôle facturation.
 *
 * Les factures reçues dans la période — vérifiées ou non par l'assistant(e) —,
 * leurs contrôles SIRET, RIB et montant, les heures des formateurs salariés
 * (« Bulletin de paie »), et l'export de la période. Le pôle y contrôle une
 * facture, la valide ou la renvoie au formateur ; les lots partent de
 * « Circuit & transmission ». Tout vient de `GET /api/factures-formateurs/reception`.
 */
export function FacFormateursPage(): JSX.Element {
  const { site } = usePerimetre();
  const toast = useToast();
  const [parametres, setParametres] = useSearchParams();
  const [filtres, setFiltres] = useState<Filtres>(SANS_FILTRE);
  const [page, setPage] = useState(1);
  const [taillePage, setTaillePage] = useTaillePage();
  const [selection, setSelection] = useState<string | null>(null);
  const [dossierOuvert, setDossierOuvert] = useState<string | null>(null);
  const [exportEnCours, setExportEnCours] = useState(false);
  const [erreurExport, setErreurExport] = useState<string | null>(null);
  const titreControle = useRef<HTMLHeadingElement>(null);
  const focaliser = useRef(false);
  const recherche = useValeurDifferee(filtres.recherche.trim(), 300);
  const demandes: FiltresReception = { site, ...filtres, recherche };
  const { data, isLoading, isFetching, error } = useReceptionFactures(demandes);

  useEffect(
    () => setPage(1),
    [site, taillePage, recherche, filtres.periode, filtres.du, filtres.au, filtres.etat, filtres.type],
  );

  // Un lien de notification ouvre la facture ; l'adresse se vide une fois lue.
  useEffect(() => {
    const facture = parametres.get('facture');
    if (!facture) return;
    setDossierOuvert(facture);
    setParametres(
      (avant) => {
        const suivants = new URLSearchParams(avant);
        suivants.delete('facture');
        return suivants;
      },
      { replace: true },
    );
  }, [parametres, setParametres]);

  // « Contrôler » porte le focus sur la carte de contrôle, à droite ou sous la liste.
  useEffect(() => {
    if (!focaliser.current) return;
    focaliser.current = false;
    titreControle.current?.focus();
  }, [selection]);

  const controler = (factureId: string): void => {
    focaliser.current = true;
    setSelection(factureId);
  };

  const changer = <C extends keyof Filtres>(cle: C, valeur: Filtres[C]): void =>
    setFiltres((avant) => ({ ...avant, [cle]: valeur }));
  const choisirPeriode = (periode: ChoixPeriodeReception): void =>
    setFiltres((avant) => ({
      ...avant,
      periode,
      // Des dates libres partent de la période affichée.
      du: periode === 'LIBRE' && !avant.du ? (data?.periode.du ?? '') : avant.du,
      au: periode === 'LIBRE' && !avant.au ? (data?.periode.au ?? '') : avant.au,
    }));

  const lignes: Ligne[] = data
    ? [
        ...data.factures.map((facture): Ligne => ({ nature: 'FACTURE', facture })),
        ...data.salaries.map((paie): Ligne => ({ nature: 'PAIE', paie })),
      ]
    : [];
  const affichees = lignes.slice((page - 1) * taillePage, page * taillePage);
  const choisie = data?.factures.find((facture) => facture.id === selection) ?? null;
  const incomplete = periodeIncomplete(filtres);
  const filtreActif =
    filtres.periode !== 'MOIS' || filtres.etat !== '' || filtres.type !== '' || filtres.recherche !== '';

  const exporter = async (): Promise<void> => {
    setErreurExport(null);
    setExportEnCours(true);
    try {
      const { nomFichier } = await exporterReception(demandes);
      toast(`${nomFichier} téléchargé`);
    } catch (refus) {
      setErreurExport(refus instanceof ErreurApi ? refus.message : 'L’export n’a pas pu être produit.');
    } finally {
      setExportEnCours(false);
    }
  };

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Factures formateurs"
        sousTitre="Pôle facturation · réception, contrôle, validation puis transmission à la Direction"
        actions={
          <>
            <BoutonSecondaire
              onClick={() => void exporter()}
              disabled={exportEnCours || incomplete || lignes.length === 0}
            >
              <FileSpreadsheet className="mr-1.5 h-4 w-4" aria-hidden="true" />
              {exportEnCours ? 'Export…' : 'Exporter la période'}
            </BoutonSecondaire>
            {data?.peutValider && data.aTransmettre > 0 ? (
              <Link to="/fac-circuit" className={CLASSE_LIEN_PRINCIPAL}>
                Transmettre à la Direction ({data.aTransmettre})
              </Link>
            ) : (
              <Link to="/fac-circuit" className={CLASSE_LIEN_SECONDAIRE}>
                Circuit & transmission
              </Link>
            )}
          </>
        }
      />

      <BarreSites />

      {erreurExport && <MessageErreur message={erreurExport} />}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Compteur
          libelle="À vérifier"
          valeur={data?.compteurs.aVerifier.nombre ?? '—'}
          ton={data && data.compteurs.aVerifier.nombre > 0 ? 'red' : 'navy'}
          detail="assistant(e) en attente"
        />
        <Compteur
          libelle="Vérifiées"
          valeur={data?.compteurs.verifiees.nombre ?? '—'}
          ton="warm"
          detail="prêtes pour validation"
        />
        <Compteur
          libelle="Transmises Direction"
          valeur={data?.compteurs.transmisesDirection.nombre ?? '—'}
          ton="blue"
          detail="en attente de paiement"
        />
        <Compteur
          libelle={data && data.periode.choix !== 'MOIS' ? 'Montant de la période' : 'Montant du mois'}
          valeur={data ? euros(data.compteurs.periode.montant) : '—'}
          ton="mint"
          detail={
            data
              ? `${pluriel(data.compteurs.periode.formateurs, 'formateur')} · ${pluriel(data.compteurs.periode.factures, 'facture')}`
              : 'Factures reçues'
          }
        />
      </div>

      <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-3">
        <div className="min-w-0 xl:col-span-2">
          <Carte
            titre="Factures reçues"
            actions={data && <span className="text-xs text-codialis-gray">{data.periode.libelle}</span>}
          >
            <div className="flex flex-wrap items-center gap-2 px-4 py-3 sm:px-6">
              <ChampFiltre
                valeur={filtres.periode}
                onChange={(valeur) => choisirPeriode(valeur as ChoixPeriodeReception)}
                etiquette="Période de réception"
              >
                {(data?.options.periodes ?? PERIODES_PAR_DEFAUT).map((option) => (
                  <option key={option.choix} value={option.choix}>
                    {option.libelle}
                  </option>
                ))}
              </ChampFiltre>
              {filtres.periode === 'LIBRE' && (
                <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
                  <ChampDate libelle="Du" valeur={filtres.du} max={filtres.au || undefined} onChange={(du) => changer('du', du)} />
                  <ChampDate libelle="au" valeur={filtres.au} min={filtres.du || undefined} onChange={(au) => changer('au', au)} />
                </div>
              )}
              <ChampFiltre
                valeur={filtres.etat}
                onChange={(valeur) => changer('etat', valeur as EtatReception | '')}
                etiquette="État"
              >
                <option value="">Tous les états</option>
                {data?.options.etats.map((option) => (
                  <option key={option.valeur} value={option.valeur}>
                    {option.libelle}
                  </option>
                ))}
              </ChampFiltre>
              <ChampFiltre
                valeur={filtres.type}
                onChange={(valeur) => changer('type', valeur as TypeFacture | '')}
                etiquette="Type de formation"
              >
                <option value="">CFA et OF</option>
                <option value="CFA">CFA</option>
                <option value="OF">OF</option>
              </ChampFiltre>
              <ChampRecherche
                valeur={filtres.recherche}
                onChange={(valeur) => changer('recherche', valeur)}
                etiquette="Rechercher un formateur ou un numéro de facture"
                placeholder="Formateur ou n° de facture…"
              />
              {filtreActif && (
                <button
                  type="button"
                  onClick={() => setFiltres(SANS_FILTRE)}
                  className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg px-3 text-xs font-semibold text-codialis-sky hover:underline"
                >
                  <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                  Réinitialiser
                </button>
              )}
            </div>

            {incomplete && filtres.du && filtres.au && filtres.du > filtres.au && (
              <div className="px-4 py-3 sm:px-6">
                <MessageErreur message="Le premier jour de la période doit précéder le dernier." />
              </div>
            )}
            {isLoading && (
              <div className="px-4 py-5 sm:px-6">
                <Chargement libelle="Chargement des factures…" />
              </div>
            )}
            {error && (
              <div className="px-4 py-3 sm:px-6">
                <MessageErreur message={messageRefus(error, 'Les factures n’ont pas pu être chargées.')} />
              </div>
            )}
            {data && !error && lignes.length === 0 && (
              <p className="px-4 py-8 text-center text-sm text-codialis-gray sm:px-6">
                {filtres.etat || filtres.type || filtres.recherche
                  ? 'Aucune facture ni bulletin de paie ne correspond aux filtres.'
                  : 'Aucune facture reçue sur la période.'}
              </p>
            )}

            {data && lignes.length > 0 && (
              <div className={`transition-opacity ${isFetching ? 'opacity-70' : ''}`}>
                {/* Sous l'ordinateur, une carte par ligne : le tableau y défilerait de côté. */}
                <ul className="divide-y divide-gray-100 lg:hidden">
                  {affichees.map((ligne) =>
                    ligne.nature === 'FACTURE' ? (
                      <CarteFacture
                        key={ligne.facture.id}
                        facture={ligne.facture}
                        choisie={ligne.facture.id === selection}
                        onControler={() => controler(ligne.facture.id)}
                      />
                    ) : (
                      <CartePaie key={ligne.paie.cle} paie={ligne.paie} />
                    ),
                  )}
                </ul>
                <div className="relative hidden overflow-x-auto lg:block">
                  <table className="w-full min-w-[600px] text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        {['Référence', 'Formateur', 'Statut prof.', 'Heures', 'Montant', 'Type', 'État'].map((colonne) => (
                          <th
                            key={colonne}
                            className={`${CLASSE_TH} ${colonne === 'Statut prof.' ? CLASSE_STATUT_LARGE : ''}`}
                          >
                            {colonne}
                          </th>
                        ))}
                        <th className={`${CLASSE_TH} text-right`}>
                          <span className="sr-only">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {affichees.map((ligne) =>
                        ligne.nature === 'FACTURE' ? (
                          <RangeeFacture
                            key={ligne.facture.id}
                            facture={ligne.facture}
                            choisie={ligne.facture.id === selection}
                            onControler={() => controler(ligne.facture.id)}
                          />
                        ) : (
                          <RangeePaie key={ligne.paie.cle} paie={ligne.paie} />
                        ),
                      )}
                    </tbody>
                  </table>
                </div>
                <Pagination
                  page={page}
                  pages={Math.max(1, Math.ceil(lignes.length / taillePage))}
                  total={lignes.length}
                  affiches={affichees.length}
                  taillePage={taillePage}
                  onPage={setPage}
                  onChangerTaille={setTaillePage}
                  uniteLabel="ligne"
                />
              </div>
            )}
          </Carte>
        </div>

        <div className="min-w-0 space-y-6">
          <CarteControle
            key={choisie?.id ?? 'aucune'}
            facture={choisie}
            titreRef={titreControle}
            onOuvrirDossier={() => choisie && setDossierOuvert(choisie.id)}
            onFait={(message) => toast(message)}
          />

          {data && (
            <Carte titre="Fréquence de traitement" sansBordure>
              <div className="space-y-2 p-4 sm:p-6">
                {data.rythmes.map((rythme) => (
                  <div
                    key={rythme.type}
                    className="flex items-center justify-between gap-3 rounded-xl bg-gray-50 px-3 py-2.5"
                  >
                    <span className="min-w-0 text-sm text-codialis-navy">{rythme.detail}</span>
                    <Pastille texte={rythme.libelle} ton={tonType(rythme.type)} />
                  </div>
                ))}
              </div>
            </Carte>
          )}
        </div>
      </div>

      {data && (
        <p className="text-[11px] text-codialis-gray">
          Période : {data.periode.libelle} · une facture compte dans la période de sa réception (premier dépôt du
          formateur) · ses contrôles sont relus aujourd’hui et ne bloquent rien · « Bulletin de paie » : heures des
          fiches séquence validées des formateurs salariés, par date de séance · les lots partent de « Circuit &
          transmission ».
        </p>
      )}

      {dossierOuvert && (
        <DossierFactureFormateur factureId={dossierOuvert} onFermer={() => setDossierOuvert(null)} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Lignes du registre
// ---------------------------------------------------------------------------

function EtatFacture({ facture }: { facture: LigneReception }): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-1">
      <Pastille texte={facture.etatLibelle} ton={facture.etatTon} />
      {facture.alertes > 0 && <Pastille texte={libelleAlertes(facture.alertes)} ton="warning" />}
    </div>
  );
}

function BoutonControler({ facture, onControler }: { facture: LigneReception; onControler: () => void }): JSX.Element {
  return (
    <button type="button" onClick={onControler} className={CLASSE_ACTION}>
      Contrôler{' '}
      <span className="sr-only">
        la facture n° {facture.numero} de {facture.formateur.nom}
      </span>
    </button>
  );
}

function RangeeFacture({
  facture,
  choisie,
  onControler,
}: {
  facture: LigneReception;
  choisie: boolean;
  onControler: () => void;
}): JSX.Element {
  return (
    <tr aria-current={choisie ? 'true' : undefined} className={`transition hover:bg-gray-50 ${choisie ? 'bg-codialis-mint/5' : ''}`}>
      <td className={CLASSE_TD}>
        <div className="whitespace-nowrap font-mono text-xs text-codialis-gray">{facture.numero}</div>
        <div className="whitespace-nowrap text-[11px] text-codialis-gray">reçue le {dateReunion(facture.recueLe)}</div>
      </td>
      <td className={CLASSE_TD}>
        <div className="font-semibold text-codialis-navy">{facture.formateur.nom}</div>
        <div className="text-[11px] text-codialis-gray 2xl:hidden">{facture.statutProfessionnel}</div>
        <PastilleSite site={facture.siteCode} />
      </td>
      <td className={`${CLASSE_TD} ${CLASSE_STATUT_LARGE} text-xs text-codialis-gray`}>{facture.statutProfessionnel}</td>
      <td className={`${CLASSE_TD} whitespace-nowrap font-mono text-xs text-codialis-gray`}>
        {heures(facture.heuresValidees)}
      </td>
      <td className={`${CLASSE_TD} whitespace-nowrap text-xs font-semibold text-codialis-navy`}>
        {euros(facture.montant)}
      </td>
      <td className={CLASSE_TD}>
        {facture.typeLibelle ? <Pastille texte={facture.typeLibelle} ton={tonType(facture.type)} /> : '—'}
      </td>
      <td className={CLASSE_TD}>
        <EtatFacture facture={facture} />
      </td>
      <td className={`${CLASSE_TD} text-right`}>
        <BoutonControler facture={facture} onControler={onControler} />
      </td>
    </tr>
  );
}

function RangeePaie({ paie }: { paie: LigneBulletinDePaie }): JSX.Element {
  return (
    <tr className="transition hover:bg-gray-50">
      <td className={`${CLASSE_TD} font-mono text-xs text-codialis-gray`}>—</td>
      <td className={CLASSE_TD}>
        <div className="font-semibold text-codialis-navy">{paie.formateur.nom}</div>
        <div className="text-[11px] text-codialis-gray 2xl:hidden">{paie.statutProfessionnel}</div>
        <PastilleSite site={paie.siteCode} />
      </td>
      <td className={`${CLASSE_TD} ${CLASSE_STATUT_LARGE} text-xs text-codialis-gray`}>{paie.statutProfessionnel}</td>
      <td className={`${CLASSE_TD} whitespace-nowrap font-mono text-xs text-codialis-gray`}>{heures(paie.heures)}</td>
      <td className={`${CLASSE_TD} text-xs text-codialis-gray`}>—</td>
      <td className={CLASSE_TD}>
        <Pastille texte={paie.typeLibelle} ton={tonType(paie.type)} />
      </td>
      <td className={CLASSE_TD}>
        <Pastille texte={paie.etatLibelle} ton={paie.etatTon} />
      </td>
      <td className={CLASSE_TD} />
    </tr>
  );
}

function CarteFacture({
  facture,
  choisie,
  onControler,
}: {
  facture: LigneReception;
  choisie: boolean;
  onControler: () => void;
}): JSX.Element {
  return (
    <li aria-current={choisie ? 'true' : undefined} className={`space-y-2 px-4 py-3.5 sm:px-6 ${choisie ? 'bg-codialis-mint/5' : ''}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-codialis-navy">
            {facture.formateur.nom}
            <PastilleSite site={facture.siteCode} />
          </div>
          <div className="text-[11px] text-codialis-gray">
            Facture n° {facture.numero} · {facture.statutProfessionnel} · reçue le {dateReunion(facture.recueLe)}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="whitespace-nowrap font-display text-sm font-bold text-codialis-navy">{euros(facture.montant)}</div>
          <div className="text-[11px] text-codialis-gray">{heures(facture.heuresValidees)}</div>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1">
          {facture.typeLibelle && <Pastille texte={facture.typeLibelle} ton={tonType(facture.type)} />}
          <EtatFacture facture={facture} />
        </div>
        <BoutonControler facture={facture} onControler={onControler} />
      </div>
    </li>
  );
}

function CartePaie({ paie }: { paie: LigneBulletinDePaie }): JSX.Element {
  return (
    <li className="space-y-2 px-4 py-3.5 sm:px-6">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-codialis-navy">
            {paie.formateur.nom}
            <PastilleSite site={paie.siteCode} />
          </div>
          <div className="text-[11px] text-codialis-gray">
            {paie.statutProfessionnel} · {pluriel(paie.fiches, 'fiche validée', 'fiches validées')}
          </div>
        </div>
        <div className="shrink-0 whitespace-nowrap font-mono text-xs text-codialis-gray">{heures(paie.heures)}</div>
      </div>
      <div className="flex flex-wrap items-center gap-1">
        <Pastille texte={paie.typeLibelle} ton={tonType(paie.type)} />
        <Pastille texte={paie.etatLibelle} ton={paie.etatTon} />
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Contrôle de la facture choisie
// ---------------------------------------------------------------------------

function Donnee({ libelle, children }: { libelle: string; children: ReactNode }): JSX.Element {
  return (
    <div className="flex min-w-0 justify-between gap-3 text-sm">
      <dt className="shrink-0 text-codialis-gray">{libelle}</dt>
      <dd className="min-w-0 break-words text-right font-medium text-codialis-navy">{children}</dd>
    </div>
  );
}

/**
 * Carte « Contrôle » de la maquette : ce que la facture choisie déclare, ses
 * contrôles, puis les gestes du pôle — valider, renvoyer. Le dossier complet
 * (IBAN en clair, fiches, historique, PDF, gestes de la Direction) s'ouvre dans
 * la fenêtre de contrôle, dont la lecture est tracée.
 */
function CarteControle({
  facture,
  titreRef,
  onOuvrirDossier,
  onFait,
}: {
  facture: LigneReception | null;
  titreRef: RefObject<HTMLHeadingElement>;
  onOuvrirDossier: () => void;
  onFait: (message: string) => void;
}): JSX.Element {
  const valider = useValiderFactureFormateur();
  const renvoyer = useRenvoyerFactureFormateur();
  const [motif, setMotif] = useState(false);

  return (
    <section aria-labelledby="titre-controle" className="overflow-hidden rounded-2xl bg-white shadow-card">
      <div className="border-b border-gray-100 px-4 py-3.5 sm:px-6 sm:py-4">
        <h2 id="titre-controle" ref={titreRef} tabIndex={-1} className="font-display min-w-0 font-bold text-codialis-navy">
          {facture ? `Contrôle · ${facture.numero}` : 'Contrôle'}
        </h2>
      </div>

      {!facture ? (
        <p className="px-4 py-5 text-sm text-codialis-gray sm:px-6">
          Choisissez « Contrôler » sur une facture pour voir son SIRET, son RIB, son montant et leurs contrôles.
        </p>
      ) : (
        <div className="space-y-4 p-4 sm:p-6">
          <div className="flex flex-wrap items-center gap-2">
            <Pastille texte={facture.etatLibelle} ton={facture.etatTon} />
            {facture.typeLibelle && <Pastille texte={facture.typeLibelle} ton={tonType(facture.type)} />}
            <PastilleSite site={facture.siteCode} />
          </div>
          <dl className="space-y-3">
            <Donnee libelle="Formateur">{facture.formateur.nom}</Donnee>
            <Donnee libelle="Période">
              Du {jourCourt(facture.debut)} au {jourCourt(facture.fin)}
            </Donnee>
            <Donnee libelle="SIRET">
              <span className="font-mono">{facture.siret}</span>
            </Donnee>
            <Donnee libelle="IBAN">
              <span className="font-mono">{facture.ibanMasque ?? 'Illisible'}</span>
            </Donnee>
            <Donnee libelle="Séquences validées">{facture.nombreFiches}</Donnee>
            <Donnee libelle="Heures facturées">{heures(facture.heuresValidees)}</Donnee>
            <Donnee libelle="Montant">{euros(facture.montant)}</Donnee>
          </dl>

          <ControlesFacture controles={facture.controles} />
          {facture.alertes === 0 && facture.nombreFiches > 0 && (
            <p className="rounded-xl bg-codialis-mint/10 p-3 text-xs text-codialis-navy">
              Cohérence vérifiée : heures facturées = heures des fiches séquence validées par le responsable pédagogique.
            </p>
          )}

          {facture.statut === 'VERIFIEE' && facture.refusValidation && (
            <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800">{facture.refusValidation}</p>
          )}
          {valider.error !== null && valider.error !== undefined && (
            <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-800">
              {messageRefus(valider.error, 'La facture n’a pas pu être validée.')}
            </p>
          )}
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap xl:flex-col 2xl:flex-row">
            {facture.statut === 'VERIFIEE' && (
              <BoutonPrincipal
                onClick={() =>
                  valider.mutate(facture.id, {
                    onSuccess: () => onFait('Facture validée · elle part au prochain lot pour la Direction'),
                  })
                }
                disabled={facture.refusValidation !== null || valider.isPending}
              >
                <Check className="mr-1.5 h-4 w-4" aria-hidden="true" />
                {valider.isPending ? 'Validation…' : 'Valider'}
              </BoutonPrincipal>
            )}
            {facture.refusRenvoi === null && (
              <BoutonSecondaire onClick={() => setMotif(true)}>
                <Undo2 className="mr-1.5 h-4 w-4" aria-hidden="true" />
                Renvoyer
              </BoutonSecondaire>
            )}
            <BoutonSecondaire onClick={onOuvrirDossier}>
              <FolderOpen className="mr-1.5 h-4 w-4" aria-hidden="true" />
              Ouvrir le dossier
            </BoutonSecondaire>
          </div>
          {facture.statut === 'TRANSMISE' && (
            <p className="text-[11px] text-codialis-gray">
              Elle attend la vérification de l’assistant(e) pédagogique ou administratif, dans « Formateurs ».
            </p>
          )}
        </div>
      )}

      {facture && motif && (
        <ModaleMotif
          titre={`Renvoyer la facture n° ${facture.numero}`}
          sousTitre={`${facture.formateur.nom} · le motif lui est adressé dans son espace et par e-mail`}
          libelle="Motif du renvoi"
          aide="La facture revient au formateur, « à corriger ». Corrigée, elle repasse par la vérification de l’assistant(e), puis par vous."
          bouton="Renvoyer au formateur"
          enCours={renvoyer.isPending}
          erreur={renvoyer.error}
          onConfirmer={(texte) =>
            renvoyer.mutate(
              { factureId: facture.id, motif: texte },
              {
                onSuccess: () => {
                  onFait('Facture renvoyée au formateur');
                  setMotif(false);
                },
              },
            )
          }
          onFermer={() => setMotif(false)}
        />
      )}
    </section>
  );
}
